import { useState, useEffect, useRef } from 'react'
import amharicLogo from '../../images/Amharic Kidase.webp'
import tigrinyaLogo from '../../images/kidase-GeezTigrinyaicon-LightBlueBG.png'
import latinLogo from '../../images/kidase-GeezLatin icon-WhiteBG.png'
import { t, tEn } from '../i18n/translations'
import { useLanguage } from '../context/LanguageContext'

const LOGOS = { tigrinya: tigrinyaLogo, amharic: amharicLogo, latin: latinLogo }

const LANGUAGES = [
  { key: 'tigrinya', native: 'ትግርኛ', sub: 'Tigrinya' },
  { key: 'amharic', native: 'አማርኛ', sub: 'Amharic' },
  { key: 'latin', native: 'English', sub: 'Latin' },
]

const WELCOME_AUDIO_URL = 'https://firebasestorage.googleapis.com/v0/b/geez-sirate-kidase.appspot.com/o/audios%2FCD-1-Track01-Slide_6.mp3?alt=media&token=35596c34-7092-467a-aac0-451263610156'

export default function WelcomeScreen({ onEnter }) {
  const { appLanguage, changeAppLanguage } = useLanguage()
  const hasChosenLang = !!localStorage.getItem('sk_app_language')
  const hasSeenWelcome = !!localStorage.getItem('sk_welcome_seen')

  const [visible, setVisible] = useState(true)
  const [fadeOut, setFadeOut] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  // Show picker if user never chose a language; otherwise show welcome content
  const [showPicker, setShowPicker] = useState(!hasChosenLang)
  const audioRef = useRef(null)

  const logo = LOGOS[appLanguage] || tigrinyaLogo

  useEffect(() => {
    // Skip welcome entirely only if user has both chosen language AND seen welcome
    if (hasChosenLang && hasSeenWelcome) {
      setVisible(false)
      onEnter()
    }
    setIsMobile(window.matchMedia('(max-width: 767px)').matches)
  }, [])

  function handlePickLanguage(lang) {
    changeAppLanguage(lang)
    setShowPicker(false)
  }

  function handleBackToPicker() {
    setShowPicker(true)
  }

  function togglePlay() {
    const audio = audioRef.current
    if (!audio) return
    if (isPlaying) {
      audio.pause()
    } else {
      audio.play()
    }
  }

  function handleEnter() {
    if (audioRef.current) {
      audioRef.current.pause()
    }
    setFadeOut(true)
    localStorage.setItem('sk_welcome_seen', '1')
    setTimeout(() => {
      setVisible(false)
      onEnter()
    }, 500)
  }

  if (!visible) return null

  return (
    <div className={`welcome-screen ${fadeOut ? 'fade-out' : ''}`}>
      <div className="welcome-content">

        {showPicker ? (
          <div className="lang-picker">
            <h2 className="lang-picker-title">Choose Language / ቋንቋ ይምረጡ</h2>
            <div className="lang-picker-options">
              {LANGUAGES.map(lang => (
                <button
                  key={lang.key}
                  className={`lang-picker-btn ${appLanguage === lang.key ? 'lang-picker-btn-active' : ''}`}
                  onClick={() => handlePickLanguage(lang.key)}
                >
                  <img className="lang-picker-logo" src={LOGOS[lang.key]} alt="" />
                  <span className="lang-picker-native">{lang.native}</span>
                  <span className="lang-picker-sub">{lang.sub}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <button className="welcome-back-btn" onClick={handleBackToPicker}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              <span>{t('langPickerTitle')}</span>
            </button>

            <div className="welcome-logo">
              <img src={logo} alt="Sirate Kidase Tutor" />
            </div>

            <h1 className="welcome-title" title={tEn('welcomeTitle')}>{t('welcomeTitle')}</h1>

            {isMobile && (
              <div className="welcome-audio">
                <audio
                  ref={audioRef}
                  src={WELCOME_AUDIO_URL}
                  preload="auto"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => setIsPlaying(false)}
                />
                <button className="welcome-play-btn" onClick={togglePlay}>
                  {isPlaying ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
                  )}
                </button>
                <span className="welcome-audio-label">+ በስመ አብ በወልድ ወመንፈስ ቅዱስ</span>
              </div>
            )}

            <div className="welcome-features">
              <div className="welcome-feature">
                <span className="welcome-feature-icon">&#9654;</span>
                <span title={tEn('welcomeFeature1')}>{t('welcomeFeature1')}</span>
              </div>
              <div className="welcome-feature">
                <span className="welcome-feature-icon">&#9998;</span>
                <span title={tEn('welcomeFeature2')}>{t('welcomeFeature2')}</span>
              </div>
              <div className="welcome-feature">
                <span className="welcome-feature-icon">&#127908;</span>
                <span title={tEn('welcomeFeature3')}>{t('welcomeFeature3')}</span>
              </div>
            </div>

            <button className="welcome-enter-btn" onClick={handleEnter} title={tEn('welcomeEnter')}>
              {t('welcomeEnter')}
            </button>

            <p className="welcome-version">{t('welcomeVersion')}</p>

            <div className="welcome-credits">
              <p>Developer: Yemane Russom</p>
              <p>Kidase Audio: Kesis Yohannes Negassi</p>
            </div>

            <div className="welcome-footer">
              <div className="welcome-social">
                <a href="https://www.youtube.com/@geezword" target="_blank" rel="noopener noreferrer" title="YouTube">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
                <a href="https://www.facebook.com/yemane.russom/" target="_blank" rel="noopener noreferrer" title="Facebook">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
              </div>
              <a href="https://geezword.com" target="_blank" rel="noopener noreferrer" className="welcome-geezword">
                geezword.com
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
